import { Badge, Button, ButtonGroup, Col, Container, Form, ListGroup, Modal, OverlayTrigger, Pagination, Row, Tab, Tabs, Tooltip } from "react-bootstrap";
import { AppAuthStateProps } from '../../../types/propTypes';

import pLimit from "p-limit";
import { useEffect, useState } from "react";
import { callFhirPatient, FhirPatientVisit } from "../../../amplify/appsync/fetch-sync-fhir";
import { callQuery, GET_FOLLOWED_PATIENTS_BY_USERNAME_WITH_ASSESSMENTS, GET_FOLLOWED_PATIENTS_BY_USERNAME_WITH_FILTERS, Note } from "../../../amplify/appsync/query";
import { PatientVisit, PatientVisitMetadata, PatientVisitSurveyMetadata } from '../../../amplify/appsync/search-patients';
import { getDefaultAugmentedPatient, updatePatientWithMrnToName } from "../../cohort/trackboardUtils";

import { faCaretDown, faCaretUp, faFilter, faGear, faRotateRight, faSprout } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getHourMinuteDifference, getLatestTime, getLocalDateStrFromDate, getLocalTimeStrFromDate, getSqlDateStrFromDateStr, subtractHours } from "../../../utils/time";
import { SectionQuickView } from "../../cohort/llm/SectionQuickView";
import { DISPOSITION_MAPPING } from "../../ops/metricsPlotOpts";
import './MyDashboard.scss';
import { EXPLAIN_REVIEW_ADMIT_DC, EXPLAIN_REVIEW_EDD, EXPLAIN_REVIEW_REVISIT } from "../../../amplify/appsync/write";
import PersonalIdentifier from "../../common/PersonalIdentifier";
import { dashboardLLMCache } from "../../dashboard/llm/cache";
import { getNotableLabels } from "../../../utils/notableLabels";
import { AssessmentType } from "../../../config/notableLabels";

const POLL_INTERVAL_MS = 30000;
const DEFAULT_PAGE_SIZE = 10;

const RELATIVE_DATE_OPTIONS: RelativeDateOption[] = [
    { label: 'Past 1 day', value: '1d', days: 1 },
    { label: 'Past 3 days', value: '3d', days: 3 },
    { label: 'Past 7 days', value: '7d', days: 7 },
    { label: 'Past 14 days', value: '14d', days: 14 },
    { label: 'Past 30 days', value: '30d', days: 30 },
    { label: 'Past 90 days', value: '90d', days: 90 },
    { label: 'Custom range', value: 'custom', days: 0 }
];

const SURVEY_TYPE_MAPPING: any = {
    [EXPLAIN_REVIEW_EDD]: "ED Discharge Assessment",
    [EXPLAIN_REVIEW_ADMIT_DC]: "ED Admit Assessment",
    [EXPLAIN_REVIEW_REVISIT]: "ED Revisit Assessment",
};

interface FilterSortParams {
    startDate?: string;
    endDate?: string;
    dispo?: string;
    sort?: string;
}

interface RelativeDateOption {
    label: string;
    value: string;
    days: number;
}

// Add this at the top of your file, outside the component
// This creates a module-level cache that persists across re-renders
const fhirPatientCache: Map<string, FhirPatientVisit> = new Map();

// Add this helper function outside the component
const getCachedOrFetchPatient = async (mrn: string): Promise<FhirPatientVisit> => {
    // Check cache first
    if (fhirPatientCache.has(mrn)) {
        return fhirPatientCache.get(mrn)!;
    }
    
    // Fetch from API if not in cache
    const result = await callFhirPatient({ mrn });
    
    // Store in cache
    fhirPatientCache.set(mrn, result);
    
    return result;
};

export const MyDashboard = (props: AppAuthStateProps) => {

    const [isLoading, setIsLoading] = useState(false);
    const [allActivePatients, setAllActivePatients] = useState<(PatientVisit & FhirPatientVisit & PatientVisitMetadata)[]>([]);
    const [allDepartedPatients, setAllDepartedPatients] = useState<(PatientVisit & FhirPatientVisit & PatientVisitMetadata)[]>([]);
    const [allAssessmentsPatients, setAllAssessmentsPatients] = useState<(PatientVisit & FhirPatientVisit & PatientVisitMetadata & PatientVisitSurveyMetadata)[]>([]);

    const [activeExpanded, setActiveExpanded] = useState(true);
    const [departedExpanded, setDepartedExpanded] = useState(true);
    const [tab, setTab] = useState<any>(() => {
        // Check URL for tab parameter on initial load
        const urlParams = new URLSearchParams(window.location.search);
        const tabParam = urlParams.get('tab');
        return tabParam === 'assessments' ? 'assessments' : 'followed';
    });
    const [assessmentFilterStatus, setAssessmentFilterStatus] = useState<'complete' | 'incomplete' | 'all'>('all');

    // Pagination state
    const [activePage, setActivePage] = useState(1);
    const [departedPage, setDepartedPage] = useState(1);
    const [assessmentPage, setAssessmentPage] = useState(1);
    const [activeTotal, setActiveTotal] = useState(0);
    const [departedTotal, setDepartedTotal] = useState(0);
    const [assessmentTotal, setAssessmentTotal] = useState(0);

    // Filter and sort state
    const [activeFilters, setActiveFilters] = useState<FilterSortParams>({
        startDate: getSqlDateStrFromDateStr(subtractHours(new Date(), 30 * 24)),
        endDate: getSqlDateStrFromDateStr(new Date()),
    });
    const [departedFilters, setDepartedFilters] = useState<FilterSortParams>({
        startDate: getSqlDateStrFromDateStr(subtractHours(new Date(), 30 * 24)),
        endDate: getSqlDateStrFromDateStr(new Date()),
    });
    const [showFilterModal, setShowFilterModal] = useState<'active' | 'departed' | null>(null);
    const [tempFilters, setTempFilters] = useState<FilterSortParams>({});
    const [selectedRelativeDate, setSelectedRelativeDate] = useState<string>('30d');

    const [activeMrnToName, setActiveMrnToName] = useState<any>({});
    const [departedMrnToName, setDepartedMrnToName] = useState<any>({});
    const [assessmentMrnToName, setAssessmentMrnToName] = useState<any>({});
    const [selectedPatient, setSelectedPatient] = useState<any>(null);
    const [notableLabelsMap, setNotableLabelsMap] = useState<Record<string, number>>({});

    const loadPatientNames = async (patients: PatientVisit[], type: string) => {
        const limit = pLimit(2);
        const localMrnToName: any = type === "active" ? {
            ...activeMrnToName,
        } : type === "departed" ? {
            ...departedMrnToName,
        } : {
            ...assessmentMrnToName,
        };

        const promises: any[] = [];
        for (let patient of patients) {
            const mrn = patient.mrn;
            
            // Check if we already have this MRN in our local state
            if (!(mrn in localMrnToName)) {
                promises.push(limit(() => {
                    return getCachedOrFetchPatient(mrn)
                        .then((pt: FhirPatientVisit) => {
                            localMrnToName[patient.mrn] = pt;
                            
                            // Update the names slowly, to avoid hogging the bandwidth
                            if (Object.keys(localMrnToName).length % 10 === 0) {
                                if (type === "active") {
                                    setActiveMrnToName({ ...localMrnToName });
                                } else if (type === "departed") {
                                    setDepartedMrnToName({ ...localMrnToName });
                                } else {
                                    setAssessmentMrnToName({ ...localMrnToName });
                                }
                            }
                            return pt;
                        })
                        .catch((err: any) => {
                            console.error(`Error fetching patient ${mrn}:`, err);
                            return {};
                        });
                }));
            }
        }

        const result = await Promise.all(promises);
        
        // Final update with all results
        if (type === "active") {
            setActiveMrnToName(localMrnToName);
        } else if (type === "departed") {
            setDepartedMrnToName(localMrnToName);
        } else {
            setAssessmentMrnToName(localMrnToName);
        }
    };

    // Function to check if two sets are equal
    function setsAreEqual(setA: any, setB: any) {
        if (setA.size !== setB.size) return false;
        for (let item of setA) {
            if (!setB.has(item)) return false;
        }
        return true;
    }

    const fetchAssessments = async (page: number = 1, filterStatus?: string) => {
        const effectiveFilter = filterStatus ?? assessmentFilterStatus;

        setIsLoading(true);
        const offset = (page - 1) * DEFAULT_PAGE_SIZE;
        const response = await callQuery({
            operation: GET_FOLLOWED_PATIENTS_BY_USERNAME_WITH_ASSESSMENTS,
            limit: DEFAULT_PAGE_SIZE,
            offset: offset,
            filter_status: effectiveFilter,
        });

        const patients = response.patients || response; // Handle different response formats
        const total = response.total || patients.length;
        
        setAssessmentTotal(total);
        setIsLoading(false);

        const augmentedPatients = patients.map((p: PatientVisit) => {
            return getDefaultAugmentedPatient(p);
        });
        
        // If patients haven't changed, don't refresh
        // Build set of existing CSNs
        const existingCSNs = new Set(allAssessmentsPatients.map(p => p.csn));

        // Build set of updated CSNs
        const updatedCSNs = new Set(augmentedPatients.map((p: PatientVisit) => p.csn));
        const csnsChanged = !setsAreEqual(existingCSNs, updatedCSNs);

        if (csnsChanged) {
            setAllAssessmentsPatients(augmentedPatients);
            loadPatientNames(patients, "assessments");
        }

        // Pre-load notable labels for all assessments
        loadNotableLabels(augmentedPatients);

        return augmentedPatients;
    };

    // Function to pre-load notable labels for assessment list
    const loadNotableLabels = async (patients: (PatientVisit & PatientVisitSurveyMetadata)[]) => {
        const limit = pLimit(3); // Limit concurrent requests
        const newNotableLabels: Record<string, number> = {};

        const loadPromises = patients.map(patient =>
            limit(async () => {
                if (!patient.survey_type || !patient.csn) return;

                try {
                    const cachedData = await dashboardLLMCache.get(
                        patient.survey_type,
                        patient,
                        props.tenant || 'stanford'
                    );

                    if (cachedData) {
                        const notableCount = getNotableLabels(
                            patient.survey_type as AssessmentType,
                            cachedData
                        ).length;
                        const key = `${patient.csn}_${patient.survey_type}`;
                        newNotableLabels[key] = notableCount;
                    }
                } catch (e) {
                    // Silently fail - notable labels are not critical
                    console.log(`Could not load notable labels for ${patient.csn}`);
                }
            })
        );

        await Promise.all(loadPromises);

        // Update state with all loaded notable labels
        setNotableLabelsMap(prev => ({
            ...prev,
            ...newNotableLabels
        }));
    };

    const fetchActivePatients = async (page: number = 1, filters: FilterSortParams = {}) => {
        const offset = (page - 1) * DEFAULT_PAGE_SIZE;
        const response = await callQuery({
            operation: GET_FOLLOWED_PATIENTS_BY_USERNAME_WITH_FILTERS,
            active: true,
            limit: DEFAULT_PAGE_SIZE,
            offset: offset,
            filters: {
                startDate: filters.startDate,
                endDate: filters.endDate,
                ...(filters.dispo !== undefined && { dispo: filters.dispo }),
            },
            sort: filters.sort
        });

        const patients = response.patients || response; // Handle different response formats
        const total = response.total || patients.length;
        
        setActiveTotal(total);
        
        const augmentedPatients = patients.map((p: PatientVisit) => {
            return getDefaultAugmentedPatient(p);
        });
        
        // If patients haven't changed, don't refresh
        // Build set of existing CSNs
        const existingCSNs = new Set(allActivePatients.map(p => p.csn));

        // Build set of updated CSNs
        const updatedCSNs = new Set(augmentedPatients.map((p: PatientVisit) => p.csn));
        const csnsChanged = !setsAreEqual(existingCSNs, updatedCSNs);

        if (csnsChanged) {
            setAllActivePatients(augmentedPatients);
            loadPatientNames(patients, "active");
        }

        return augmentedPatients;
    };

    const fetchDepartedPatients = async (page: number = 1, filters: FilterSortParams = {}) => {
        const offset = (page - 1) * DEFAULT_PAGE_SIZE;
        const response = await callQuery({
            operation: GET_FOLLOWED_PATIENTS_BY_USERNAME_WITH_FILTERS,
            active: false,
            limit: DEFAULT_PAGE_SIZE,
            offset: offset,
            filters: {
                startDate: filters.startDate,
                endDate: filters.endDate,
                ...(filters.dispo !== undefined && { dispo: filters.dispo }),
            },
            sort: filters.sort
        });

        const patients = response.patients || response; // Handle different response formats
        const total = response.total || patients.length;
        
        setDepartedTotal(total);
        
        const augmentedPatients = patients.map((p: PatientVisit) => {
            return getDefaultAugmentedPatient(p);
        });

        // If patients haven't changed, don't refresh
        // Build set of existing CSNs
        const existingCSNs = new Set(allDepartedPatients.map(p => p.csn));

        // Build set of updated CSNs
        const updatedCSNs = new Set(augmentedPatients.map((p: PatientVisit) => p.csn));
        const csnsChanged = !setsAreEqual(existingCSNs, updatedCSNs);

        if (csnsChanged) {
            setAllDepartedPatients(augmentedPatients);
            loadPatientNames(patients, "departed");
        }

        return augmentedPatients;
    };

    const refreshBaseData = async () => {
        if (isLoading) return; // guard to prevent overlap

        setIsLoading(true);
        try {
            await Promise.all([
                fetchActivePatients(activePage, activeFilters),
                fetchDepartedPatients(departedPage, departedFilters)
            ]);
        } catch (error) {
            console.error('Error fetching patients:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAssessmentPageChange = async (page: number) => {
        setAssessmentPage(page);
        await fetchAssessments(page);
    };

    const handleActivePageChange = async (page: number) => {
        setActivePage(page);
        await fetchActivePatients(page, activeFilters);
    };

    const handleDepartedPageChange = async (page: number) => {
        setDepartedPage(page);
        await fetchDepartedPatients(page, departedFilters);
    };

    const openFilterModal = (type: 'active' | 'departed') => {
        const currentFilters = type === 'active' ? activeFilters : departedFilters;
        setTempFilters({ ...currentFilters });
        
        // Determine which relative date option is currently selected
        if (currentFilters.startDate && currentFilters.endDate) {
            const now = new Date();
            const startDate = new Date(currentFilters.startDate);
            const diffTime = Math.abs(now.getTime() - startDate.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            const matchingOption = RELATIVE_DATE_OPTIONS.find(option => option.days === diffDays);
            setSelectedRelativeDate(matchingOption ? matchingOption.value : 'custom');
        } else {
            setSelectedRelativeDate('7d'); // Default
        }
        
        setShowFilterModal(type);
    };

    const handleRelativeDateChange = (value: string) => {
        setSelectedRelativeDate(value);
        
        if (value === 'custom') {
            // Don't change the dates, let user set them manually
            return;
        }
        
        const option = RELATIVE_DATE_OPTIONS.find(opt => opt.value === value);
        if (option && option.days > 0) {
            const now = new Date();
            const startDate = new Date(now);
            startDate.setDate(startDate.getDate() - option.days);
            
            setTempFilters({
                ...tempFilters,
                startDate: getSqlDateStrFromDateStr(startDate),
                endDate: getSqlDateStrFromDateStr(now)
            });
        }
    };

    const handleDispoChange = (value: string) => {
        setTempFilters({
            ...tempFilters,
            dispo: value,
        });
    };

    const applyFilters = async () => {
        if (showFilterModal === 'active') {
            setActiveFilters({ ...tempFilters });
            setActivePage(1);
            await fetchActivePatients(1, tempFilters);
        } else if (showFilterModal === 'departed') {
            setDepartedFilters({ ...tempFilters });
            setDepartedPage(1);
            await fetchDepartedPatients(1, tempFilters);
        }
        setShowFilterModal(null);
    };

    const resetFilters = () => {
        setTempFilters({});
        setSelectedRelativeDate('7d');
        
        // Set default 7-day range
        const now = new Date();
        const startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 7);
        
        const formatDateTimeLocal = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        };
        
        setTempFilters({
            startDate: formatDateTimeLocal(startDate),
            endDate: formatDateTimeLocal(now)
        });
    };

    useEffect(() => {
        refreshBaseData();
    }, []);

    useEffect(() => {
        if (tab !== "followed") {
            fetchAssessments(assessmentPage);
            return; // no polling, no cleanup needed
        }

        // Only run polling when tab is "followed"
        if (isLoading) return; // just skip if still loading initial data

        const interval = setInterval(() => {
            refreshBaseData();
        }, POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [tab, activePage, departedPage, assessmentPage, activeFilters, departedFilters]);

    useEffect(() => {
        setAllActivePatients((pts: any) => {
            return [
                ...pts.map(((patient: (PatientVisit & FhirPatientVisit & PatientVisitMetadata)) => {
                    return updatePatientWithMrnToName(patient, activeMrnToName);
                })),
            ];
        });
    }, [activeMrnToName]);

    useEffect(() => {
        setAllDepartedPatients((pts: any) => {
            return [
                ...pts.map(((patient: (PatientVisit & FhirPatientVisit & PatientVisitMetadata)) => {
                    return updatePatientWithMrnToName(patient, departedMrnToName);
                })),
            ];
        });
    }, [departedMrnToName]);

    useEffect(() => {
        setAllAssessmentsPatients((pts: any) => {
            return [
                ...pts.map(((patient: (PatientVisit & FhirPatientVisit & PatientVisitMetadata)) => {
                    return updatePatientWithMrnToName(patient, assessmentMrnToName);
                })),
            ];
        });
    }, [assessmentMrnToName]);

    const getAcuityColor = (patient: PatientVisit) => {
        if (patient.triage_acuity) {
            const val = patient.triage_acuity;
            if (val.startsWith("1")) {
                return "red";
            } else if (val.startsWith("2")) {
                return "orange";
            } else if (val.startsWith("3")) {
                return "rgb(205, 183, 43)";
            }
        }
        return "gray";
    }

    const getElapsedTime = (patient: PatientVisit) => {
        const dt = new Date(patient.arrival_time as string);
        let currTime = getLatestTime(patient, props.tenant);
        return `${getHourMinuteDifference(dt, new Date(currTime))}`;
    }

    const getEdDispo = (patient: PatientVisit & PatientVisitMetadata) => {
        let badgeVariant = "light";
        let className = "";
        if (patient && patient.ed_dispo) {
            const val = DISPOSITION_MAPPING[patient.ed_dispo];
            if (val === "Admit") {
                badgeVariant = "danger";
            } else if (val === "Discharge") {
                badgeVariant = "success";
            } else if (val === "Transfer") {
                badgeVariant = "info";
            }
        } else {
            className = "trackboard-bg-other";
            return <></>;
        }
        return <Badge bg={badgeVariant} className={className}>{patient.ed_dispo}</Badge>
    }

    // Alternative version with more visual emphasis on completion status
    const renderAssessmentElement = (patient: PatientVisit & FhirPatientVisit & PatientVisitMetadata & PatientVisitSurveyMetadata) => {
        const getTimeSinceDeparture = () => {
            if (!patient.departure_time) return "Recently";
            const departed = new Date(patient.departure_time);
            const now = new Date();
            const diffHours = Math.floor((now.getTime() - departed.getTime()) / (1000 * 60 * 60));

            if (diffHours < 1) return "< 1h";
            if (diffHours < 24) return `${diffHours}h`;
            const diffDays = Math.floor(diffHours / 24);
            return `${diffDays}d`;
        };

        const isComplete = patient && patient.response_count && patient.question_count &&  Number(patient.response_count) === Number(patient.question_count);
        const progressPercent = patient === undefined ? 0 : Number(patient.question_count) > 0
            ? (Number(patient.response_count) / Number(patient.question_count)) * 100
            : 0;

        // Get notable labels count from the map
        const notableKey = `${patient.csn}_${patient.survey_type}`;
        const notableCount = notableLabelsMap[notableKey] || 0;

        return (
            <Container
                key={patient.csn + patient.survey_type}
                className={`patient-row ${patient.has_updates ? 'unread' : 'read'} ${selectedPatient && (patient.csn === selectedPatient.csn && patient.survey_type === selectedPatient.survey_type) && 'selected'}`}
                onClick={() => handleSelectPatient(patient)}
            >
                <Row>
                    <Col md={9}>
                        <div className="patient-row-title">
                            <PersonalIdentifier identifier={patient.family ?? "-"} />, <PersonalIdentifier identifier={patient.given ?? "-"} />
                        </div>
                        <div className="patient-row-subdetails">
                            <span style={{fontWeight: 500}}>{patient.survey_type && SURVEY_TYPE_MAPPING[patient.survey_type]}</span> • {getTimeSinceDeparture()}
                            {notableCount > 0 && <Badge bg="warning" text="dark" className="ms-2" style={{fontSize: '0.7em'}}>{notableCount} notable</Badge>}
                        </div>
                    </Col>
                    <Col md={3} className="text-end">
                        <div style={{fontSize: '0.85em', fontWeight: 600, color: isComplete ? '#28a745' : '#856404'}}>
                            {patient.response_count}/{patient.question_count}
                        </div>
                        <div style={{fontSize: '0.75em', color: '#6c757d'}}>
                            {isComplete ? 'Done' : 'Pending'}
                        </div>
                    </Col>
                </Row>
                {/* Optional: Progress bar for incomplete assessments */}
                {!isComplete && (
                    <Row className="mt-1">
                        <Col>
                            <div style={{
                                width: '100%',
                                height: '3px',
                                backgroundColor: '#e9ecef',
                                borderRadius: '2px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    width: `${progressPercent}%`,
                                    height: '100%',
                                    backgroundColor: '#ffc107',
                                    transition: 'width 0.3s ease'
                                }} />
                            </div>
                        </Col>
                    </Row>
                )}
            </Container>
        )
    }

    const renderPatientElement = (patient: PatientVisit & FhirPatientVisit & PatientVisitMetadata) => {
        return (
            <Container
                key={patient.csn}
                className={`patient-row ${patient.has_updates ? 'unread' : 'read'} ${selectedPatient && patient.csn === selectedPatient.csn && 'selected'}`}
                onClick={() => handleSelectPatient(patient)}
            >
                <Row>
                    <Col md={11}>
                        <div className="patient-row-title">
                            <PersonalIdentifier identifier={patient.family ?? "-"} />, <PersonalIdentifier identifier={patient.given ?? "-"} /> ({patient.age}{patient.gender}) {!patient.departure_time && <span style={{color: getAcuityColor(patient)}}>[{patient.triage_acuity ? patient.triage_acuity.substring(0, 1) : "-"}]</span>}
                        </div>
                        <div className="patient-row-subdetails">
                            Arrived {patient.arrival_time ? `${getLocalDateStrFromDate(new Date(patient.arrival_time))} ${getLocalTimeStrFromDate(new Date(patient.arrival_time))}` : ""} • {patient.bed} • {patient.chief_complaint} • {getElapsedTime(patient)}
                        </div>
                    </Col>
                    <div className="patient-row-actions">{getEdDispo(patient)}</div>
                </Row>
            </Container>
        );
    }

    const renderPagination = (currentPage: number, totalItems: number, onPageChange: (page: number) => void) => {
        const totalPages = Math.ceil(totalItems / DEFAULT_PAGE_SIZE);
        
        if (totalPages <= 1) return null;

        const items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        
        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => onPageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" />);
            }
        }

        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => onPageChange(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => onPageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return (
            <div className="d-flex justify-content-center mt-3">
                <Pagination size="sm">
                    <Pagination.Prev 
                        disabled={currentPage === 1}
                        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                    />
                    {items}
                    <Pagination.Next 
                        disabled={currentPage === totalPages}
                        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                    />
                </Pagination>
            </div>
        );
    };

    const updatePatientWithNote = (note: Note) => {
        setAllActivePatients(activePatients =>
            activePatients.map(pt => 
                pt.csn === note.csn ? { ...pt, note: note.note } : pt
            )
        );
        setAllDepartedPatients(departedPatients =>
            departedPatients.map(pt => 
                pt.csn === note.csn ? { ...pt, note: note.note } : pt
            )
        );
        setAllAssessmentsPatients(patients =>
            patients.map(pt => 
                pt.csn === note.csn ? { ...pt, note: note.note } : pt
            )
        );
    };

    const updatePatientAsRead = (csn: string) => {
        setAllActivePatients(activePatients =>
            activePatients.map(pt => 
                pt.csn === csn ? { ...pt, has_updates: false } : pt
            )
        );
        setAllDepartedPatients(departedPatients =>
            departedPatients.map(pt => 
                pt.csn === csn ? { ...pt, has_updates: false } : pt
            )
        );
    };

    const handleSelectPatient = (patientVisit: PatientVisit) => {
        updatePatientAsRead(patientVisit.csn);
        setSelectedPatient(patientVisit);
    }

    const handleFollowStatusChange = (patientVisit: any, isAdded: boolean) => {
        if (isAdded) {
            if (!patientVisit.departure_time) {
                setAllActivePatients(activePatients => {
                    return [...activePatients, {...patientVisit}];
                });
            } else {
                setAllDepartedPatients(departedPatients => {
                    return [...departedPatients, {...patientVisit}];
                });
            }
            setAllDepartedPatients(patients => {
                return [...patients, {...patientVisit}];
            });
        } else {
            setAllActivePatients(activePatients => {
                return [...activePatients.filter(p => p.csn !== patientVisit.csn)];
            });
            setAllDepartedPatients(departedPatients => {
                return [...departedPatients.filter(p => p.csn !== patientVisit.csn)];
            });
            setAllAssessmentsPatients(assessmentPatients => {
                return [...assessmentPatients.filter(p => p.csn !== patientVisit.csn)];
            });
        }
    };

    const getNumFiltersApplied = (active: boolean) => {
        let filter = activeFilters;
        if (!active) {
            filter = departedFilters;
        }
        let numFilters = 0;
        if (filter.startDate) {
            numFilters++;
        }
        if (filter.dispo) {
            numFilters++;
        }
        return numFilters;
    };

    const eddSurveyResultsUpdateCallback = (csn: string, result: Record<string, any>) => {
        setAllAssessmentsPatients(assessmentPatients => {
            const patients: any = [];
            for (let p of assessmentPatients) {
                if (p.csn === csn && p.survey_type === "explain_review_edd") {
                    patients.push({
                        ...p,
                        response_count: String(Object.keys(result).length),
                    });
                } else {
                    patients.push({
                        ...p,
                    });
                }
            }
            return patients;
        });
    };

    const admitSurveyResultsUpdateCallback = (csn: string, result: Record<string, any>) => {
        setAllAssessmentsPatients(assessmentPatients => {
            const patients: any = [];
            for (let p of assessmentPatients) {
                if (p.csn === csn && p.survey_type === "explain_review_admit_dc") {
                    patients.push({
                        ...p,
                        response_count: String(Object.keys(result).length),
                    });
                } else {
                    patients.push({
                        ...p,
                    });
                }
            }
            return patients;
        });
    };

    const revisitSurveyResultsUpdateCallback = (csn: string, result: Record<string, any>) => {
        setAllAssessmentsPatients(assessmentPatients => {
            const patients: any = [];
            for (let p of assessmentPatients) {
                if (p.csn === csn && p.survey_type === "explain_review_revisit") {
                    patients.push({
                        ...p,
                        response_count: String(Object.keys(result).length),
                    });
                } else {
                    patients.push({
                        ...p,
                    });
                }
            }
            return patients;
        });
    };

    const notableLabelsUpdateCallback = (csn: string, surveyType: string, count: number) => {
        const key = `${csn}_${surveyType}`;
        setNotableLabelsMap(prev => ({
            ...prev,
            [key]: count
        }));
    };

    return (
        <>
            <div className="my-dashboard">
                {/* Sidebar: Patient List */}
                <div className="bg-light border-end overflow-auto patient-list">

                    <Tabs
                        activeKey={tab}
                        onSelect={(k) => setTab(k ?? "")}
                        className="modern-tabs mt-1 mb-2"
                    >
                        <Tab eventKey="followed" title="Followed">
                            <div className="d-flex justify-content-between">
                                <div className="patient-list-header" onClick={() => {setActiveExpanded(!activeExpanded)}}>
                                    Active Patients ({activeTotal}) <FontAwesomeIcon icon={activeExpanded ? faCaretDown : faCaretUp} />
                                </div>
                                <div className="d-flex">
                                    <OverlayTrigger
                                        placement="bottom"
                                        overlay={
                                            <Tooltip>
                                                Refresh Patients
                                            </Tooltip>
                                        }
                                    >
                                        <Button
                                            variant="link-secondary"
                                            onClick={() => {refreshBaseData()}}
                                            size="sm"
                                            className="dashboard-round-btn dashboard-round-btn-gray"
                                            style={{marginTop: '6px'}}
                                        >
                                            <FontAwesomeIcon icon={faRotateRight} spin={isLoading} />
                                        </Button>
                                    </OverlayTrigger>

                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Sort & Filter Patients
                                                </Tooltip>
                                            }
                                        >
                                            <Button
                                                variant="link-secondary"
                                                onClick={() => openFilterModal('active')}
                                                size="sm"
                                                className="dashboard-round-btn dashboard-round-btn-gray me-2"
                                                style={{marginTop: '6px'}}
                                            >
                                                <FontAwesomeIcon icon={faFilter} />
                                            </Button>
                                        </OverlayTrigger>
                                        {getNumFiltersApplied(true) > 0 && (
                                            <Badge
                                                bg="info"
                                                className="dashboard-round-btn-badge"
                                                pill
                                            >
                                                {getNumFiltersApplied(true)}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {
                                allActivePatients.length === 0 && (
                                    <div className="patient-list-no-data">
                                        No active followed patients
                                    </div>
                                )
                            }
                            {
                                activeExpanded && (
                                    <>
                                        <ListGroup variant="flush">
                                            {allActivePatients.map((patient, idx) => {
                                                return renderPatientElement(patient)
                                            })}
                                        </ListGroup>
                                        {renderPagination(activePage, activeTotal, handleActivePageChange)}
                                    </>
                                )
                            }


                            <div className="d-flex justify-content-between">
                                <div className="patient-list-header mt-3" onClick={() => {setDepartedExpanded(!departedExpanded)}}>
                                    Previous Patients ({departedTotal}) <FontAwesomeIcon icon={departedExpanded ? faCaretDown : faCaretUp} />
                                </div>
                                <div className="d-flex">
                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Sort & Filter Patients
                                                </Tooltip>
                                            }
                                        >
                                            <Button
                                                variant="link-secondary"
                                                onClick={() => openFilterModal('departed')}
                                                size="sm"
                                                className="dashboard-round-btn dashboard-round-btn-gray me-2"
                                                style={{marginTop: '16px'}}
                                            >
                                                <FontAwesomeIcon icon={faFilter} />
                                            </Button>
                                        </OverlayTrigger>
                                        {getNumFiltersApplied(true) > 0 && (
                                            <Badge
                                                bg="info"
                                                className="dashboard-round-btn-badge"
                                                style={{marginTop: '10px'}}
                                                pill
                                            >
                                                {getNumFiltersApplied(false)}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {
                                allDepartedPatients.length === 0 && (
                                    <div className="patient-list-no-data">
                                        No departed followed patients
                                    </div>
                                )
                            }
                            {
                                departedExpanded && (
                                    <>
                                        <ListGroup variant="flush">
                                            {allDepartedPatients.map((patient, idx) => {
                                                return renderPatientElement(patient)
                                            })}
                                        </ListGroup>
                                        {renderPagination(departedPage, departedTotal, handleDepartedPageChange)}
                                    </>
                                )
                            }
                        </Tab>
                        <Tab eventKey="assessments" title="Assessments">
                            <ButtonGroup size='sm' className="mb-2 ps-3 pe-3 w-100">
                                <Button 
                                    variant={assessmentFilterStatus === "all" ? "secondary" : "outline-secondary"}
                                    onClick={() => {
                                        setAssessmentFilterStatus("all");
                                        fetchAssessments(assessmentPage, "all");
                                    }}
                                >
                                    All
                                </Button>
                                <Button 
                                    variant={assessmentFilterStatus === "complete" ? "secondary" : "outline-secondary"}
                                    onClick={() => {
                                        setAssessmentFilterStatus("complete");
                                        fetchAssessments(assessmentPage, "complete");
                                    }}
                                >
                                    Complete
                                </Button>
                                <Button 
                                    variant={assessmentFilterStatus === "incomplete" ? "secondary" : "outline-secondary"}
                                    onClick={() => {
                                        setAssessmentFilterStatus("incomplete");
                                        fetchAssessments(assessmentPage, "incomplete");
                                    }}
                                >
                                    Incomplete
                                </Button>
                            </ButtonGroup>
                            {
                                isLoading && (
                                    <div className="patient-list-no-data text-center mt-4">
                                        <FontAwesomeIcon icon={faGear} spin /> Loading....
                                    </div>
                                )
                            }
                            {
                                !isLoading && allAssessmentsPatients.length === 0 && (
                                    <div className="patient-list-no-data">
                                        No assessments for you yet
                                    </div>
                                )
                            }
                            {
                                !isLoading && activeExpanded && (
                                    <>
                                        <ListGroup variant="flush">
                                            {allAssessmentsPatients.map((patient, idx) => {
                                                return renderAssessmentElement(patient)
                                            })}
                                        </ListGroup>
                                        {renderPagination(assessmentPage, assessmentTotal, handleAssessmentPageChange)}
                                    </>
                                )
                            }
                        </Tab>
                    </Tabs>
                </div>

                {/* Main Panel: Patient Details */}
                <div className="p-3 patient-details">
                    {selectedPatient ? (
                        <>
                            <SectionQuickView
                                patient={selectedPatient}
                                updatePatientWithNote={updatePatientWithNote}
                                handleFollowStatusChange={handleFollowStatusChange}
                                showPatientMetadata={false} user={props.user}
                                tenant={props.tenant} ws={props.ws}
                                eddSurveyResultsUpdateCallback={eddSurveyResultsUpdateCallback}
                                admitSurveyResultsUpdateCallback={admitSurveyResultsUpdateCallback}
                                revisitSurveyResultsUpdateCallback={revisitSurveyResultsUpdateCallback}
                                notableLabelsUpdateCallback={notableLabelsUpdateCallback}
                            />
                        </>
                    ) : (
                        <div className="text-muted mt-5 text-center">
                            <div>
                                <FontAwesomeIcon icon={faSprout} size="4x" />
                            </div>
                            <div className="mt-4">
                                <strong>Select a followed patient to view details</strong>
                            </div>
                            <div className="mt-2">
                                Follow a patient on the <a href="#" onClick={() => window.open("/trackboard-v2", "_blank")}>trackboard</a>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Filter/Sort Modal */}
            <Modal show={!!showFilterModal} onHide={() => setShowFilterModal(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>Filter & Sort {showFilterModal === 'active' ? 'Active' : 'Previous'} Patients</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Date Range</Form.Label>
                            <Form.Select
                                value={selectedRelativeDate}
                                onChange={(e) => handleRelativeDateChange(e.target.value)}
                            >
                                {RELATIVE_DATE_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        
                        {selectedRelativeDate === 'custom' && (
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Start Date & Time</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={tempFilters.startDate || ''}
                                            onChange={(e) => setTempFilters({...tempFilters, startDate: e.target.value})}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>End Date & Time</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={tempFilters.endDate || ''}
                                            onChange={(e) => setTempFilters({...tempFilters, endDate: e.target.value})}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                        
                        {selectedRelativeDate !== 'custom' && (
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Start Date & Time</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={tempFilters.startDate || ''}
                                            readOnly
                                            className="bg-light"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>End Date & Time</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={tempFilters.endDate || ''}
                                            readOnly
                                            className="bg-light"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Disposition</Form.Label>
                            <Form.Select
                                value={tempFilters.dispo || ""}
                                onChange={(e) => handleDispoChange(e.target.value)}
                            >
                                <option value={""}>
                                    No Filter
                                </option>
                                {Object.keys(DISPOSITION_MAPPING).sort().map(option => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Sort By</Form.Label>
                            <Form.Select
                                value={tempFilters.sort || ''}
                                onChange={(e) => setTempFilters({...tempFilters, sort: e.target.value})}
                            >
                                <option value="">Default</option>
                                <option value="arrival_time_asc">Arrival Time (Ascending)</option>
                                <option value="arrival_time_desc">Arrival Time (Descending)</option>
                                <option value="name_asc">Name (A-Z)</option>
                                <option value="name_desc">Name (Z-A)</option>
                                <option value="acuity_asc">Acuity (Low to High)</option>
                                <option value="acuity_desc">Acuity (High to Low)</option>
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={resetFilters}>
                        Reset
                    </Button>
                    <Button variant="outline-secondary" onClick={() => setShowFilterModal(null)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={applyFilters}>
                        Apply Filters
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
